/* -------------------------------- Libraries ------------------------------- */

/* ------------------------------- Components ------------------------------- */
import { Box, Grid, Stack, Typography } from "@mui/material";
import { AquariusTextarea } from "@smartrr/shared/customerPortal/components/AquariusTextarea";

/* ----------------------------- Helpers & Utils ---------------------------- */
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { countriesData } from "@smartrr/shared/data/countries";
import { ICreateGift } from "@smartrr/shared/entities/Gift";
import { MailingAddressType } from "@smartrr/shared/entities/MailingAddress";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { IShopifyLikeMailingAddress } from "@smartrr/shared/interfaces/Address";
import { ISmartrrCountry } from "@smartrr/shared/interfaces/Country";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { deleteGift, sendAsGift } from "@customer-app/state/actionCreators/customerPurchaseStates";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerDispatch } from "@smartrr/customer-portal/src/_state/typedCustomerReduxHooks";

import { AddressForm } from "../../../../Settings/AddressForm";
import { CenterText } from "../CenterText";

const MAX_TEXTAREA_CHARACTERS = 160;

interface IGiftViewProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  onCancel: () => void;
  onConfirm: () => void;
}

export const GiftView = ({ delivery, onCancel, onConfirm }: IGiftViewProps): JSX.Element => {
  const localizeShorthand = "subscriptions.yourSubscriptions.orderOptionsModal";
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();

  const { purchaseState } = delivery;

  const gift = delivery.purchaseState.gifts?.find(gift => {
    return gift.skdIdx === delivery.indexFromScheduleStart;
  });

  const stId = purchaseState.id;
  const skdIdx = delivery.indexFromScheduleStart;

  const noteRef = useRef<HTMLTextAreaElement | null>(null);

  const [isValid, setIsValid] = useState<boolean>(!!gift ?? false);
  const [countriesList, setCountriesList] = useState<ISmartrrCountry[]>();
  const [addressFormValues, setAddressFormValues] = useState<IShopifyLikeMailingAddress | undefined>(
    gift ? { ...gift.to, address2: gift.to.address2 ?? "" } : undefined
  );

  const [notifyBy] = useState<"email" | "text" | "email+text">(gift?.notifyBy ?? "email+text");

  const loadCountries = async () => {
    if (!countriesList) {
      // TODO: filter for only the countries this shop ships to
      // https://shopify.dev/docs/admin-api/rest/reference/store-properties/country#index-2021-01
      const countries = countriesData;
      setCountriesList(countries);
    }
  };
  const onDeleteGift = useCallback(() => {
    if (gift) {
      dispatch(
        deleteGift({
          ...gift,
          stId,
          cpsId: purchaseState.id,
        })
      );
      toast(localize(`${localizeShorthand}.giftView.ungiftedToast`));
      closeModal();
    }
  }, [gift, dispatch]);

  const onSendGift = useCallback(
    (gift: ICreateGift) => {
      dispatch(sendAsGift(gift, localize(`${localizeShorthand}.giftView.giftedToast`), toast));
      closeModal();
    },
    [dispatch]
  );

  useEffect(() => {
    loadCountries();
  }, []);

  return (
    <React.Fragment>
      {gift ? (
        <CenterText title={localize(`${localizeShorthand}.giftView.ungift`)}>
          {localize(`${localizeShorthand}.giftView.ungiftBody`, {
            name: gift.to.firstName,
          })}
        </CenterText>
      ) : (
        <Grid container spacing="15px">
          {countriesList ? (
            <React.Fragment>
              <Grid item md={12} xs={12}>
                <AddressForm
                  countriesList={countriesList!}
                  addressType={MailingAddressType.GIFTING}
                  isEditing={false}
                  onChange={(values, isAddressValid) => {
                    setAddressFormValues(values);
                    setIsValid(isAddressValid);
                  }}
                />
              </Grid>
              <Grid item md={12} xs={12}>
                <Box sx={{ marginTop: "15px" }}>
                  <Typography variant="h4" sx={{ marginBottom: "10px" }}>
                    {localize(`${localizeShorthand}.giftView.giftMessageHeader`)}
                  </Typography>
                  <AquariusTextarea
                    ref={noteRef}
                    placeholder={typedLocalize("addressForm.giftTextareaPlaceholder", {
                      max: MAX_TEXTAREA_CHARACTERS,
                    })}
                    maxLength={MAX_TEXTAREA_CHARACTERS}
                  />
                </Box>
              </Grid>
            </React.Fragment>
          ) : (
            <Stack
              sx={{
                height: "634.8px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <DotSpinner />
            </Stack>
          )}
        </Grid>
      )}
      <ConfirmCancelButtons
        disabled={!isValid}
        onCancel={() => onCancel()}
        onConfirm={() => {
          onConfirm();
          if (gift) {
            onDeleteGift();
          } else {
            const note = noteRef.current!.value;
            if (note && !addressFormValues?.email) {
              toast.error(localize(`${localizeShorthand}.giftView.emailErrorToast`));
              return;
            }
            onSendGift({
              stId,
              skdIdx,
              note,
              email: addressFormValues?.email,
              phone: addressFormValues?.phone,
              to: addressFormValues!,
              notifyBy,
            });
          }
        }}
      />
    </React.Fragment>
  );
};
